import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ENDPOINTS } from "@/api/application-api";
import { useUserToken } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";
import { ToastType } from "@/enums";
import { displayErrorToast, displayToast } from "@/utils/toast";

export interface UpdatePromptLabelProtectionRequest {
  labelId: number;
  is_protected: boolean;
  approver_role_id: number | null;
}

export interface UpdatePromptLabelProtectionResponse {
  success: boolean;
  prompt_label: {
    id: number;
    name: string;
    prompt_id: number;
    prompt_version_id: number;
    created_at: string;
    is_protected: boolean;
    approver_role_id: number | null;
  };
}

export const useUpdatePromptLabelProtection = () => {
  const userToken = useUserToken();
  const queryClient = useQueryClient();
  const activeWorkspaceId = useUser()?.activeWorkspaceId ?? 0;

  return useMutation<
    UpdatePromptLabelProtectionResponse,
    unknown,
    UpdatePromptLabelProtectionRequest
  >({
    mutationFn: async ({ labelId, is_protected, approver_role_id }) => {
      const response = await fetch(`${ENDPOINTS.legacy_prompt_labels}/${labelId}/protection`, {
        method: "PATCH",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({
          is_protected,
          approver_role_id,
        }),
      });

      return handleResponse(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [ENDPOINTS.legacy_prompt_labels],
      });
      queryClient.invalidateQueries({
        queryKey: [ENDPOINTS.legacy_prompt_versions],
      });
      queryClient.invalidateQueries({
        queryKey: [ENDPOINTS.prompt_versions(activeWorkspaceId)],
      });
      displayToast("Label configuration updated successfully", ToastType.success);
    },
    onError: (error: unknown) => {
      let errorMessage: string;
      if (typeof error === "string") {
        errorMessage = error;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      } else {
        errorMessage = "Failed to update label protection";
      }
      displayErrorToast(errorMessage);
    },
  });
};
